import * as React from 'react';
import { styled, useTheme } from '../theme-provider';
import { GalleryContext } from './index';
import { createA11yOutlineStyles } from '../helpers/a11y';
const StyledBullets = styled.div.withConfig({ displayName: "StyledBullets", componentId: "sc-vphoa0" }) `display:flex;justify-content:center;align-items:center;font-size:0;line-height:0;height:${({ theme }) => theme.size.grid001};gap:${({ theme }) => theme.size.static150};`;
const VisuallyHidden = styled.div.withConfig({ displayName: "VisuallyHidden", componentId: "sc-1bqwty" }) `clip:rect(0 0 0 0);clip-path:inset(50%);height:1px;overflow:hidden;position:absolute;white-space:nowrap;width:1px;`;
const StyledBullet = styled.button.withConfig({ displayName: "StyledBullet", componentId: "sc-cwv4jb" }) `position:relative;width:${({ theme }) => theme.size.static150};height:${({ theme }) => theme.size.static150};border:0;padding:0;border-radius:50%;background:${({ theme }) => theme.colors.background.primary};cursor:${({ isActive }) => (isActive ? 'unset' : 'pointer')};outline:none;transform:scale(${({ isActive }) => (isActive ? 2 : 1)});transition:${({ theme }) => {
    const duration = theme.animation.duration.layer;
    const timingFunction = theme.animation.timingFunction.in;
    return `all ${duration} ${timingFunction}`;
}};::before{content:'';position:absolute;top:12.5%;left:12.5%;width:75%;height:75%;border-radius:50%;background:${({ theme }) => theme.colors.button.primary.default};}:hover{::before{background:${({ theme }) => theme.colors.button.primary.hover};}}:focus{outline:none;::before{${({ theme }) => createA11yOutlineStyles(theme)};}}`;
const NavigationBullet = ({ index, isActive = false, children, length }) => {
    const { triggerChange, id: galleryID } = React.useContext(GalleryContext);
    const theme = useTheme();
    const handleClick = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        triggerChange(index);
    }, [index, triggerChange]);
    const handleKey = React.useCallback((event) => {
        var _a, _b;
        let newIndex;
        switch (event.key) {
            case 'ArrowLeft':
                newIndex =
                    (index + (theme.direction === 'rtl' ? 1 : length - 1)) % length;
                break;
            case 'ArrowRight':
                newIndex =
                    (index + (theme.direction === 'rtl' ? length - 1 : 1)) % length;
                break;
            case 'Home':
                newIndex = 0;
                break;
            case 'End':
                newIndex = length - 1;
                break;
            default:
                newIndex = null;
        }
        if (newIndex !== null) {
            event.stopPropagation();
            event.preventDefault();
            // since we add the handler to one of our bullet buttons we know the target and the siblings are HTMLElements
            (_b = (_a = event.target.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector(`:nth-child(${newIndex + 1})`)) === null || _b === void 0 ? void 0 : _b.focus();
            triggerChange(newIndex);
        }
    }, [index, length, theme.direction, triggerChange]);
    return (React.createElement(StyledBullet, { isActive: isActive, onClick: handleClick, onKeyDown: handleKey, key: `carousel-bullet-${index}`, tabIndex: isActive ? 0 : -1, "aria-pressed": isActive, "aria-controls": galleryID },
        React.createElement(VisuallyHidden, null, children)));
};
export const NavigationBullets = ({ children, ariaLabel, ariaLabelledby }) => {
    const { index } = React.useContext(GalleryContext);
    const length = React.Children.count(children);
    return (React.createElement(StyledBullets, { role: "group", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby }, React.Children.map(children, (child, bulletIndex) => (React.createElement(NavigationBullet, { index: bulletIndex, isActive: index === bulletIndex, length: length }, child)))));
};
